import { useState, useEffect, useCallback } from "react"
import type { PuzzleSummary } from "@shared/types.ts"
import { getFavorites, getRecent, toggleFavorite, deletePuzzle } from "../lib/api-client.ts"
import "../styles/library.css"

function formatDate(iso: string): string {
  const date = new Date(iso)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return "Today"
  if (diffDays === 1) return "Yesterday"

  const month = date.toLocaleDateString("en-US", { month: "short" })
  const day = date.getDate()

  if (date.getFullYear() === now.getFullYear()) return `${month} ${day}`
  return `${month} ${day}, ${date.getFullYear()}`
}

interface PuzzleLibraryProps {
  onNavigate: (path: string) => void
  currentUserId?: string
}

export function PuzzleLibrary({ onNavigate, currentUserId }: PuzzleLibraryProps) {
  const [activeTab, setActiveTab] = useState<"recent" | "favorites">("recent")
  const [recentPuzzles, setRecentPuzzles] = useState<PuzzleSummary[]>([])
  const [favoritePuzzles, setFavoritePuzzles] = useState<PuzzleSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [pendingDelete, setPendingDelete] = useState<string | null>(null)

  useEffect(() => {
    Promise.all([getRecent(), getFavorites()])
      .then(([recent, favorites]) => {
        setRecentPuzzles(recent)
        setFavoritePuzzles(favorites)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const handleToggleFavorite = useCallback(
    async (slug: string, e: React.MouseEvent) => {
      e.stopPropagation()
      const result = await toggleFavorite(slug)

      // Update both lists optimistically
      const updateList = (list: PuzzleSummary[]) =>
        list.map((p) =>
          p.slug === slug ? { ...p, isFavorited: result.favorited } : p,
        )

      setRecentPuzzles(updateList)
      if (result.favorited) {
        // Add to favorites if not already there
        setFavoritePuzzles((prev) => {
          if (prev.some((p) => p.slug === slug)) return updateList(prev)
          const puzzle = recentPuzzles.find((p) => p.slug === slug)
          return puzzle ? [{ ...puzzle, isFavorited: true }, ...prev] : prev
        })
      } else {
        setFavoritePuzzles((prev) => prev.filter((p) => p.slug !== slug))
      }
    },
    [recentPuzzles],
  )

  const handleDelete = useCallback(
    async (slug: string) => {
      try {
        await deletePuzzle(slug)
        setRecentPuzzles((prev) => prev.filter((p) => p.slug !== slug))
        setFavoritePuzzles((prev) => prev.filter((p) => p.slug !== slug))
      } catch {
        // silently fail — puzzle may already be deleted
      }
      setPendingDelete(null)
    },
    [],
  )

  const puzzles = activeTab === "recent" ? recentPuzzles : favoritePuzzles

  return (
    <section className="puzzle-library">
      <div className="library-tabs">
        <div className="library-tabs-inner">
          <button
            className={`library-tab ${activeTab === "recent" ? "library-tab--active" : ""}`}
            onClick={() => setActiveTab("recent")}
          >
            Recent
          </button>
          <button
            className={`library-tab ${activeTab === "favorites" ? "library-tab--active" : ""}`}
            onClick={() => setActiveTab("favorites")}
          >
            Favorites
          </button>
        </div>
      </div>

      <div className="puzzle-list">
        {loading ? (
          <div className="library-skeleton">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton-card" />
            ))}
          </div>
        ) : puzzles.length > 0 ? (
          puzzles.map((puzzle) => (
            <div
              key={puzzle.slug}
              className="puzzle-card"
              onClick={() => onNavigate(`/p/${puzzle.slug}`)}
            >
              <div className="puzzle-card-actions">
                <button
                  className={`puzzle-card-star ${puzzle.isFavorited ? "puzzle-card-star--active" : ""}`}
                  onClick={(e) => handleToggleFavorite(puzzle.slug, e)}
                  title={
                    puzzle.isFavorited
                      ? "Remove from favorites"
                      : "Add to favorites"
                  }
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill={puzzle.isFavorited ? "currentColor" : "none"}
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                </button>
                {currentUserId && puzzle.creatorId === currentUserId && (
                  pendingDelete === puzzle.slug ? (
                    <span className="delete-confirm" onClick={(e) => e.stopPropagation()}>
                      <button className="delete-confirm-yes" onClick={(e) => { e.stopPropagation(); handleDelete(puzzle.slug) }}>Delete</button>
                      <button className="delete-confirm-no" onClick={(e) => { e.stopPropagation(); setPendingDelete(null) }}>Cancel</button>
                    </span>
                  ) : (
                    <button
                      className="puzzle-card-delete"
                      onClick={(e) => { e.stopPropagation(); setPendingDelete(puzzle.slug) }}
                      title="Delete puzzle"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  )
                )}
              </div>

              <div className="puzzle-card-info">
                <div className="puzzle-card-title">
                  {puzzle.gridSize}&times;{puzzle.gridSize}{" "}
                  {puzzle.puzzleType.charAt(0).toUpperCase() +
                    puzzle.puzzleType.slice(1)}
                </div>
                {(puzzle.isComplete || puzzle.updatedAt) && (
                  <div className="puzzle-card-status">
                    {puzzle.isComplete ? (
                      <>
                        <span className="status-dot status-dot--complete" />
                        Finished
                      </>
                    ) : (
                      puzzle.updatedAt && (
                        <>
                          <span className="status-dot status-dot--progress" />
                          In progress
                        </>
                      )
                    )}
                  </div>
                )}
              </div>

              <div className="puzzle-card-meta">
                {formatDate(puzzle.updatedAt || puzzle.createdAt)}
              </div>

              <svg
                className="puzzle-card-arrow"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
            </div>
          ))
        ) : (
          <div className="library-empty">
            <div className="library-empty-icon">
              {activeTab === "favorites" ? (
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
              ) : (
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="3" width="7" height="7" />
                  <rect x="14" y="3" width="7" height="7" />
                  <rect x="3" y="14" width="7" height="7" />
                  <rect x="14" y="14" width="7" height="7" />
                </svg>
              )}
            </div>
            <h3>
              {activeTab === "favorites"
                ? "No favorites yet"
                : "No puzzles yet"}
            </h3>
            <p>
              {activeTab === "favorites"
                ? "Star puzzles to save them for later"
                : "Start solving to see your progress here"}
            </p>
          </div>
        )}
      </div>
    </section>
  )
}
